function createBoundedFunction(widget, originalFunction) {
  return (...rest) => {
    return originalFunction(widget, ...rest);
  };
}

function setDefaultValueForUndefined(object, keys, value = {}) {
  let objectClone = { ...object };

  keys.forEach((key) => {
    objectClone[key] = objectClone[key] || value;
  });

  return objectClone;
}

function bindWidgetToFunctions(widget, target) {
  target = target || widget;
  Object.keys(target).forEach((key) => {
    if (isFunction(target[key])) {
      let originalFunction = target[key];

      target[key] = createBoundedFunction(widget, originalFunction);
    }
  });
}

function hookMethod(widget, path, handler) {
  const { target, methodName } = parsePath(widget, path);
  const originalFunction = createBoundedFunction(widget, target[methodName]);

  target[methodName] = function (widget, ...rest) {
    return handler(widget, originalFunction, ...rest);
  };

  return originalFunction;
}

function parsePath(widget, path = '') {
  const paths = path.split('.');
  const methodName = paths.pop();
  const target = paths.reduce((target, path) => target[path] || {}, widget);

  if (!isFunction(target[methodName])) {
    throw new Error(
      `Defined path '${path}' is incorrect. Check your widget structure.`
    );
  }

  return { target, methodName };
}

function isFunction(value) {
  return typeof value === 'function';
}

async function callPluginMethod(widget, method, args) {
  for (const plugin of widget.$plugins) {
    if (isFunction(plugin[method])) {
      widget = await plugin[method](widget, ...args);
    }
  }

  return widget;
}

async function createMerkurWidget(widgetDefinition = {}) {
  widgetDefinition = setDefaultValueForUndefined(widgetDefinition, [
    '$dependencies',
    '$external',
  ]);
  widgetDefinition = setDefaultValueForUndefined(
    widgetDefinition,
    ['setup', 'create'],
    (widget) => widget
  );

  const { setup, create } = widgetDefinition;

  let widget = {
    async setup(widget, ...rest) {
      widget = await callPluginMethod(widget, 'setup', rest);

      return setup(widget, ...rest);
    },
    async create(widget, ...rest) {
      widget = await callPluginMethod(widget, 'create', rest);

      return create(widget, ...rest);
    },
    $plugins: (widgetDefinition.$plugins || []).map((pluginFactory) =>
      pluginFactory()
    ),
  };

  // TODO refactoring
  widget.name = widgetDefinition.name;
  widget.version = widgetDefinition.version;
  widget.$dependencies = widgetDefinition.$dependencies;
  widget.$external = widgetDefinition.$external;
  widget.$in = {};

  delete widgetDefinition.name;
  delete widgetDefinition.version;
  delete widgetDefinition.$dependencies;
  delete widgetDefinition.$external;
  delete widgetDefinition.$plugins;

  delete widgetDefinition.setup;
  delete widgetDefinition.create;

  widget = await widget.setup(widget, widgetDefinition);
  widget = await widget.create(widget, widgetDefinition);

  bindWidgetToFunctions(widget);
  Object.seal(widget);

  return widget;
}

function register({ name, version, createWidget }) {
  const merkur = getMerkur();

  merkur.$in.widgetFactory[name + version] = createWidget;
}

function create(widgetProperties = {}) {
  const merkur = getMerkur();
  const { name, version } = widgetProperties;
  const factory = merkur.$in.widgetFactory[name + version];

  if (!isFunction(factory)) {
    throw new Error(
      `The widget with defined name and version "${
        name + version
      }" is not defined.`
    );
  }

  return factory(widgetProperties);
}

function createMerkur({ $plugins = [] } = {}) {
  const merkur = getMerkur();

  $plugins.forEach((plugin) => {
    if (plugin && isFunction(plugin.setup)) {
      plugin.setup(merkur);
    }
  });

  return merkur;
}

function removeMerkur() {
  const globalContext = getGlobalContext();

  delete globalContext.__merkur__;
}

function getMerkur() {
  const globalContext = getGlobalContext();

  if (!globalContext.__merkur__) {
    globalContext.__merkur__ = {
      $in: {
        widgets: [],
        widgetFactory: {},
      },
      $external: {},
      $dependencies: {},
      register,
      create,
    };
  }

  return globalContext.__merkur__;
}

function getGlobalContext() {
  if (typeof globalThis !== 'undefined') {
    return globalThis;
  }
  if (typeof self !== 'undefined') {
    return self;
  }
  if (typeof window !== 'undefined') {
    return window;
  }
  if (typeof global !== 'undefined') {
    return global;
  }

  return {};
}

export { bindWidgetToFunctions, createMerkur, createMerkurWidget, getMerkur, hookMethod, isFunction, removeMerkur, setDefaultValueForUndefined };
